import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  type AnonymousSessionPolicy,
  invitationControllerResolveAnonymousSessionEntities,
  customerControllerInitializeCustomer,
} from 'korint-sdk';
import { useAnonymousSession } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';

import { AnonymousPolicySelection } from './AnonymousPolicySelection';
import { Center, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function AnonymousLandingPage({
  basePath,
  productId,
}: {
  basePath: string;
  productId: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brokerageFirmId } = useParams<{ brokerageFirmId: string }>();
  const [searchParams] = useSearchParams();
  const requestedPolicyId = searchParams.get('policyId');
  const hasInitiated = useRef(false);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [selection, setSelection] = useState<AnonymousSessionPolicy[] | null>(null);
  const { session } = useAnonymousSession();

  const buildUrl = useCallback(
    ({ customerId, policyId, branchId }: AnonymousSessionPolicy) => {
      let url = basePath
        .replace(':brokerageFirmId', brokerageFirmId ?? '')
        .replace(':customerId', customerId)
        .replace(':policyId', policyId ?? '');

      url = branchId ? url.replace(':branchId?', branchId) : url.replace('/:branchId?', '');
      return url;
    },
    [basePath, brokerageFirmId],
  );

  const initNewPolicy = useCallback(
    async (authHeaders: Record<string, string>) => {
      const created = await customerControllerInitializeCustomer(
        { ...(brokerageFirmId ? { brokerageFirmId } : {}) },
        { productId },
        { headers: authHeaders },
      );
      navigate(
        buildUrl({
          customerId: created.id,
          policyId: created.initializedWith.policy,
          branchId: created.initializedWith.branch,
        }),
        { replace: true },
      );
    },
    [brokerageFirmId, productId, navigate, buildUrl],
  );

  useEffect(() => {
    if (!session || hasInitiated.current) return;

    const authHeaders = { Authorization: `Bearer ${session.accessToken}` };

    const run = async () => {
      hasInitiated.current = true;
      try {
        const resolved = await invitationControllerResolveAnonymousSessionEntities({
          headers: authHeaders,
        });

        const resumable = (resolved?.policies ?? []).filter(({ policyId }) => !!policyId);

        // Deep link from the quote email → resume exactly that quote.
        const requested = requestedPolicyId
          ? resumable.find(({ policyId }) => policyId === requestedPolicyId)
          : undefined;
        if (requested) {
          navigate(buildUrl(requested), { replace: true });
          return;
        }

        if (resumable.length >= 1) {
          setSelection(resolved?.policies ?? []);
          return;
        }

        await initNewPolicy(authHeaders);
      } catch {
        hasInitiated.current = false;
        setError(true);
      }
    };

    run();
  }, [session, requestedPolicyId, navigate, buildUrl, initNewPolicy, retryNonce]);

  const authHeaders = session ? { Authorization: `Bearer ${session.accessToken}` } : null;

  if (error) {
    return (
      <Stack align="center" justify="center" className="top-0 left-0 bottom-0 right-0 py-32">
        <AlertCircleIcon className="w-8 h-8 text-destructive" />
        <Text size="sm" tone="muted">
          {t('anonymousSelection.error')}
        </Text>
        <button
          type="button"
          className="text-sm underline text-primary"
          onClick={() => {
            hasInitiated.current = false;
            setError(false);
            setRetryNonce(nonce => nonce + 1);
          }}
        >
          {t('anonymousSelection.retry')}
        </button>
      </Stack>
    );
  }

  if (selection && authHeaders) {
    return (
      <AnonymousPolicySelection
        policies={selection}
        authHeaders={authHeaders}
        onSelect={policy => navigate(buildUrl(policy), { replace: true })}
        onCreateNew={() => {
          initNewPolicy(authHeaders).catch(() => setError(true));
        }}
      />
    );
  }

  return (
    <Center className="top-0 left-0 bottom-0 right-0 py-32">
      <Loader2Icon className="w-8 h-8 animate-spin" />
    </Center>
  );
}
